import React, { createContext, useContext, useReducer, useEffect } from 'react'
import type { User, AuthState, SubscriptionStatus } from '@/types'
import ApiService from '@/services/api'

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  checkSubscription: () => Promise<void>
  updateUser: (updates: Partial<User>) => void
}

const AuthContext = createContext<AuthContextType | null>(null)

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'SET_SUBSCRIPTION_STATUS'; payload: SubscriptionStatus }
  | { type: 'RESTORE_SESSION'; payload: { user: User; token: string } }
  | { type: 'UPDATE_USER'; payload: Partial<User> }

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  subscriptionStatus: null,
}

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
      }
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      }
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      }
    case 'LOGOUT':
      return {
        ...initialState,
        isLoading: false,
      }
    case 'SET_SUBSCRIPTION_STATUS':
      return {
        ...state,
        subscriptionStatus: action.payload,
      }
    case 'RESTORE_SESSION':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      }
    case 'UPDATE_USER':
      if (!state.user) return state
      const updatedUser = { ...state.user, ...action.payload }
      localStorage.setItem('user', JSON.stringify(updatedUser))
      return {
        ...state,
        user: updatedUser,
      }
    default:
      return state
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState)

  useEffect(() => {
    const restoreSession = () => {
      const token = localStorage.getItem('token')
      const userStr = localStorage.getItem('user')
      
      if (token && userStr) {
        try {
          const user = JSON.parse(userStr)
          dispatch({
            type: 'RESTORE_SESSION',
            payload: { user, token }
          })
        } catch (error) {
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          dispatch({ type: 'LOGIN_FAILURE' })
        }
      } else {
        dispatch({ type: 'LOGIN_FAILURE' })
      }
    }

    restoreSession()
  }, [])

  useEffect(() => {
    if (state.isAuthenticated && state.user?.userType === 'restaurant_owner' && state.user.restaurantId) {
      checkSubscription()
    }
  }, [state.isAuthenticated, state.user])

  const login = async (email: string, password: string) => {
    dispatch({ type: 'LOGIN_START' })
    
    try {
      const response = await ApiService.login(email, password)
      
      if (response.success) {
        const { user, token } = response.data
        localStorage.setItem('token', token)
        localStorage.setItem('user', JSON.stringify(user))
        
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user, token }
        })
      } else {
        throw new Error(response.message || 'Login failed')
      }
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' })
      throw error
    }
  }

  const logout = async () => {
    try {
      await ApiService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      dispatch({ type: 'LOGOUT' })
    }
  }

  const checkSubscription = async () => {
    if (!state.user?.restaurantId) return

    // First check if subscription data is already in user object (from login)
    if (state.user.subscriptionStatus && state.user.subscriptionEndDate) {
      const now = new Date()
      const endDate = new Date(state.user.subscriptionEndDate)
      const daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      const isValid = state.user.subscriptionStatus === 'active' && daysRemaining > 0

      dispatch({
        type: 'SET_SUBSCRIPTION_STATUS',
        payload: isValid ? 'active' : 'expired'
      })
      return
    }

    // Fallback to API call if subscription data is not in user object
    try {
      const response = await ApiService.checkSubscriptionValidity(state.user.restaurantId)

      if (response.success) {
        const status = response.data.isValid ? 'active' : 'expired'
        dispatch({
          type: 'SET_SUBSCRIPTION_STATUS',
          payload: status
        })
      }
    } catch (error) {
      console.error('Subscription check error:', error)
      dispatch({
        type: 'SET_SUBSCRIPTION_STATUS',
        payload: 'expired'
      })
    }
  }

  const updateUser = (updates: Partial<User>) => {
    dispatch({
      type: 'UPDATE_USER',
      payload: updates
    })
  }

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    checkSubscription,
    updateUser,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export { AuthContext }